/**
 * Site-wide quick view modal. Loaded from snippets/quick-view-modal.liquid as type="module".
 */

const SELECTOR = '[data-quick-view-modal]'
const MOBILE_SHEET = '(max-width: 719px)'
const CLOSE_DISTANCE = 110
const CLOSE_VELOCITY = 0.55

let modal
let content
let loading
let product
let opened = false
let added = false

function lockScroll(lock) {
  document.documentElement.classList.toggle('overflow-hidden', lock)
  document.body.classList.toggle('overflow-hidden', lock)
  document.body.setAttribute('data-fluorescent-overlay-open', String(lock))
}

function clearClones() {
  document.querySelectorAll('.js-quick-add-wrapper').forEach((el) => el.classList.remove('active'))
}

function showLoading() {
  if (!content) return
  content.classList.add('empty')
  content.replaceChildren(loading ? loading.cloneNode(true) : '')
}

async function render(productUrl) {
  if (!productUrl || !content) return

  const url = `${productUrl}${productUrl.includes('?') ? '&' : '?'}view=quick-view`
  showLoading()

  try {
    const res = await fetch(url, { credentials: 'include' })
    if (!res.ok) throw new Error(`Quick view request failed (${res.status})`)

    const doc = new DOMParser().parseFromString(await res.text(), 'text/html')
    const el = doc.querySelector('[data-is-quick-view]')
    if (!el) {
      console.error('[quick-view-modal] [data-is-quick-view] not found in response')
      return
    }

    el.addEventListener('submit', () => {
      added = true
    })

    el.querySelectorAll('.product__color-swatch--sibling-product').forEach((sibling) => {
      const siblingUrl = sibling.getAttribute('href') || sibling.pathname
      if (!siblingUrl) return
      sibling.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        render(siblingUrl)
      })
    })

    product?.unload?.()
    content.classList.remove('empty')
    content.replaceChildren(el)
    modal.classList.add('animation--quick-view-revealed')

    const rendered = content.querySelector('[data-is-quick-view]')
    if (rendered && window.OneskinProduct) {
      product = new window.OneskinProduct(rendered)
    }

    document.dispatchEvent(new CustomEvent('product-item:reinit', { detail: { root: content } }))
  } catch (err) {
    console.error('[quick-view-modal] failed to load product', err)
  }
}

function open(productUrl) {
  if (!modal || !productUrl) return

  clearClones()
  opened = true
  added = false
  modal.classList.add('is-fixed', 'active', 'is-visible', 'animation--quick-view-revealed')
  modal.removeAttribute('aria-hidden')
  lockScroll(true)
  render(productUrl)
}

function close() {
  if (!modal) return

  if (opened && !added && window.oneskin?.events) {
    const title =
      modal.querySelector('[data-product-form]')?.dataset.productTitle ||
      modal.querySelector('[data-product-title]')?.dataset.productTitle ||
      ''
    window.oneskin.events.track('quick_add_abandon', {
      product_name: title,
      source_location: 'quick_view',
    })
  }

  opened = false
  added = false
  clearClones()

  const sheet = modal.querySelector('.quick-product--modal__inner')
  if (sheet) {
    sheet.classList.remove('is-dragging')
    sheet.style.transform = ''
  }

  modal.classList.remove('is-visible', 'active')
  modal.setAttribute('aria-hidden', 'true')
  lockScroll(false)

  setTimeout(() => {
    modal.classList.remove('is-fixed')
    if (content) {
      content.replaceChildren(loading || '')
      content.classList.add('empty')
    }
    product?.unload?.()
    product = null
  }, 50)
}

function bindSwipe(sheet) {
  if (!sheet) return

  let startY = 0
  let startX = 0
  let currentY = 0
  let startTime = 0
  let tracking = false
  let dragging = false

  function scrollableFrom(target) {
    let el = target instanceof Element ? target : null
    while (el && el !== sheet) {
      const style = getComputedStyle(el)
      const canScroll = /(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow)
      if (canScroll && el.scrollHeight > el.clientHeight + 1) return el
      el = el.parentElement
    }
    return sheet.querySelector('.quick-product--scroll')
  }

  sheet.addEventListener(
    'touchstart',
    (event) => {
      if (!matchMedia(MOBILE_SHEET).matches || !modal.classList.contains('active')) return
      if (event.touches.length !== 1) return

      const touch = event.touches[0]
      startY = touch.clientY
      startX = touch.clientX
      currentY = 0
      startTime = Date.now()
      dragging = false

      const scrollEl = scrollableFrom(event.target)
      tracking = !(scrollEl && scrollEl.scrollTop > 0)
    },
    { passive: true }
  )

  sheet.addEventListener(
    'touchmove',
    (event) => {
      if (!tracking) return

      const touch = event.touches[0]
      const dy = touch.clientY - startY
      const dx = touch.clientX - startX

      if (!dragging) {
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
          tracking = false
          return
        }
        if (dy <= 0) {
          tracking = false
          return
        }
        if (dy > 8) {
          dragging = true
          sheet.classList.add('is-dragging')
        } else {
          return
        }
      }

      if (dy > 0) {
        currentY = dy
        sheet.style.transform = `translateY(${dy}px)`
        event.preventDefault()
      }
    },
    { passive: false }
  )

  function endDrag() {
    if (!tracking && !dragging) return

    const velocity = currentY / Math.max(Date.now() - startTime, 1)
    sheet.classList.remove('is-dragging')
    sheet.style.transform = ''
    if (dragging && (currentY >= CLOSE_DISTANCE || velocity >= CLOSE_VELOCITY)) close()

    tracking = false
    dragging = false
    currentY = 0
  }

  sheet.addEventListener('touchend', endDrag)
  sheet.addEventListener('touchcancel', endDrag)
}

function init(root = document) {
  const node = root.querySelector(SELECTOR)
  if (!node || node.dataset.quickViewInit) return
  node.dataset.quickViewInit = 'true'

  modal = node
  content = node.querySelector('.quick-view-modal__content')
  loading = node.querySelector('.quick-view-modal-loading-indicator')

  node.querySelector('.modal__wash')?.addEventListener('click', (event) => {
    event.preventDefault()
    close()
  })
  node.querySelector('[data-modal-close]')?.addEventListener('click', (event) => {
    event.preventDefault()
    close()
  })
  node.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close()
  })

  bindSwipe(node.querySelector('.quick-product--modal__inner'))
}

document.addEventListener('quick-view:open', (event) => {
  open(event.detail?.productUrl)
})
document.addEventListener('quick-view:close', close)

init()
document.addEventListener('shopify:section:load', (event) => {
  init(event.target)
})
